import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Star, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { EmployeeDetail, EmergencyContact, PersonalInfo } from '@/types/employee';
import {
  createEmergencyContact,
  updateEmergencyContact,
  deleteEmergencyContact,
  updatePersonalContacts,
  type CreateEmergencyContactInput,
  type UpdateEmergencyContactInput,
  type UpdatePersonalContactsInput,
} from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import { usePermission } from '@/lib/permissions';
import { IconActionButton } from './icon-action-button';
import { EmergencyContactModal } from './emergency-contact-modal';
import { PersonalContactsModal } from './personal-contacts-modal';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';

interface EmergencyTabProps {
  employee: EmployeeDetail;
}

// ─────────────────────────────────────────────────────────────────────────────
// Channel config: messenger metadata + brand-coloured icons.
// Keys match `EmergencyContact`/`PersonalInfo` column names; `linkedin` keys
// PersonalInfo via `linkedinUrl` (handled at the call site).
// ─────────────────────────────────────────────────────────────────────────────

type MessengerKey = 'telegram' | 'whatsapp' | 'viber' | 'signal' | 'discord' | 'linkedin';
type ContactKey = MessengerKey | 'phone' | 'email';

type ChannelConfig = {
  label: string;
  /** Tailwind classes for the rounded icon container, both themes. */
  bg: string;
  fg: string;
  icon: ReactNode;
  /** Build a deep-link href from the raw value (otherwise the chip copies). */
  buildHref?: (value: string) => string | undefined;
  /** Render the value monospace (numeric channels). */
  mono?: boolean;
};

const TG_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M9.04 15.32L8.7 19.5c.48 0 .69-.21.94-.46l2.26-2.16 4.69 3.43c.86.47 1.47.22 1.7-.79l3.08-14.41c.3-1.27-.46-1.77-1.3-1.46L1.41 9.12c-1.24.49-1.22 1.18-.21 1.5l4.84 1.5L17.3 5.4c.53-.34 1.01-.15.62.2" />
  </svg>
);

const WA_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.5 3.5A11.4 11.4 0 0 0 12 0C5.5 0 .2 5.3.2 11.8c0 2.1.6 4.1 1.6 5.9L0 24l6.5-1.7a11.7 11.7 0 0 0 5.5 1.4c6.5 0 11.8-5.3 11.8-11.8.1-3.2-1.2-6.2-3.3-8.4M12 21.8a9.8 9.8 0 0 1-5-1.4l-.4-.2-3.8 1 1-3.7-.2-.4a9.7 9.7 0 0 1-1.5-5.2c0-5.4 4.4-9.8 9.8-9.8 2.6 0 5.1 1 7 2.9a9.8 9.8 0 0 1 2.9 6.9c-.1 5.4-4.5 9.9-9.8 9.9" />
  </svg>
);

const VB_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M11.4 0c-3.7.2-7.6 2.5-9.7 5.8-1.5 2.3-1.9 4-2 6.5-.1 1.4.2 3.4.7 4.6.4 1 1 1.7 2 2.4 1.1.7 1.1.7 1.1 2.3 0 1 .1 1.5.3 1.7.4.4.7.3 2.1-.5 2.7-1.5 4.6-2.4 4.6-2.4s.7.1 1.5.1c2.5 0 4.4-.5 6.4-1.6 5.4-3.2 6.8-9.9 3-15.1C19.1 1.3 15.4-.2 11.5 0z" />
  </svg>
);

const SG_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M21 11.5c0 1.8-.5 3.5-1.4 5l1.4 5-5.1-1.3a10 10 0 1 1 5.1-8.7z" />
  </svg>
);

const DC_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.27 5.33A18.34 18.34 0 0 0 14.66 4l-.27.46a17.6 17.6 0 0 1 4.06 1.31 17.4 17.4 0 0 0-5.04-1.5 11.6 11.6 0 0 0-1.4-.13 11.6 11.6 0 0 0-1.4.13c-1.74.2-3.42.7-5.04 1.5a17.6 17.6 0 0 1 4.06-1.31L9.34 4a18.34 18.34 0 0 0-4.61 1.33C2.18 9.4 1.4 13.36 1.6 17.27a17.13 17.13 0 0 0 5.25 2.66l1.04-1.4a10.3 10.3 0 0 1-1.66-.8c.14-.1.27-.21.4-.31a12.27 12.27 0 0 0 10.74 0c.13.1.26.21.4.31-.53.31-1.08.58-1.66.8l1.04 1.4a17.13 17.13 0 0 0 5.25-2.66c.23-4.58-.61-8.5-3.13-11.94zM8.52 15.33c-1 0-1.83-.93-1.83-2.07 0-1.14.81-2.07 1.83-2.07s1.85.94 1.83 2.07c0 1.14-.81 2.07-1.83 2.07zm6.96 0c-1 0-1.83-.93-1.83-2.07 0-1.14.81-2.07 1.83-2.07s1.85.94 1.83 2.07c0 1.14-.81 2.07-1.83 2.07z" />
  </svg>
);

const LI_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 0H5C2.2 0 0 2.2 0 5v14c0 2.7 2.2 5 5 5h14c2.8 0 5-2.3 5-5V5c0-2.8-2.2-5-5-5zM8 19H5V8h3v11zM6.5 6.7c-1 0-1.7-.8-1.7-1.7s.8-1.7 1.7-1.7 1.7.8 1.7 1.7-.7 1.7-1.7 1.7zM20 19h-3v-5.6c0-3.4-4-3.1-4 0V19h-3V8h3v1.8c1.4-2.6 7-2.8 7 2.5V19z" />
  </svg>
);

const PHONE_ICON = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const EMAIL_ICON = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const CHANNELS: Record<ContactKey, ChannelConfig> = {
  phone: {
    label: 'Phone',
    bg: 'bg-hygge/15',
    fg: 'text-hygge',
    icon: PHONE_ICON,
    buildHref: (v) => `tel:${v.replace(/\s+/g, '')}`,
    mono: true,
  },
  email: {
    label: 'Email',
    bg: 'bg-muted',
    fg: 'text-muted-foreground',
    icon: EMAIL_ICON,
    buildHref: (v) => `mailto:${v}`,
  },
  telegram: {
    label: 'Telegram',
    bg: 'bg-[#E5F1FA] dark:bg-[#1F3242]',
    fg: 'text-[#2998D3] dark:text-[#5BB8E5]',
    icon: TG_ICON,
    buildHref: (v) => {
      const stripped = v
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^t\.me\//i, '')
        .replace(/^@+/, '');
      return stripped ? `https://t.me/${stripped}` : undefined;
    },
  },
  whatsapp: {
    label: 'WhatsApp',
    bg: 'bg-[#E5F4E6] dark:bg-[#0E2E1B]',
    fg: 'text-[#25A14F] dark:text-[#25D366]',
    icon: WA_ICON,
    buildHref: (v) => {
      const digits = v.replace(/\D+/g, '');
      return digits ? `https://wa.me/${digits}` : undefined;
    },
    mono: true,
  },
  viber: {
    label: 'Viber',
    bg: 'bg-[#EFE8F4] dark:bg-[#2E1F39]',
    fg: 'text-[#7B519D] dark:text-[#B695CC]',
    icon: VB_ICON,
    mono: true,
  },
  signal: {
    label: 'Signal',
    bg: 'bg-[#E5EBF7] dark:bg-[#1A2540]',
    fg: 'text-[#3A76F0] dark:text-[#6E97E5]',
    icon: SG_ICON,
    mono: true,
  },
  discord: {
    label: 'Discord',
    bg: 'bg-[#E8EAF6] dark:bg-[#1F2447]',
    fg: 'text-[#5865F2] dark:text-[#7984F5]',
    icon: DC_ICON,
  },
  linkedin: {
    label: 'LinkedIn',
    bg: 'bg-[#E5EEF7] dark:bg-[#142A47]',
    fg: 'text-[#0A66C2] dark:text-[#4A90D9]',
    icon: LI_ICON,
    buildHref: (v) => (v.startsWith('http') ? v : undefined),
  },
};

const MESSENGER_KEYS: MessengerKey[] = [
  'telegram',
  'whatsapp',
  'viber',
  'signal',
  'discord',
  'linkedin',
];

function formatRelationship(relationship: string | undefined): string {
  if (!relationship) return 'Contact';
  return relationship.charAt(0).toUpperCase() + relationship.slice(1);
}

function getInitial(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Personal Contacts grid — 3×2 cells, one per messenger channel.
// Filled cells link out / copy; empty cells open the edit modal.
// ─────────────────────────────────────────────────────────────────────────────

interface PersonalChannelSlotProps {
  channel: ChannelConfig;
  value: string | null | undefined;
  onAdd?: () => void;
}

function PersonalChannelSlot({ channel, value, onAdd }: PersonalChannelSlotProps) {
  if (value && value.trim().length > 0) {
    const href = channel.buildHref?.(value);
    const handleCopy = () => {
      void navigator.clipboard.writeText(value);
    };
    const content = (
      <>
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
            channel.bg,
            channel.fg
          )}
        >
          {channel.icon}
        </span>
        <span className="flex min-w-0 flex-col text-left">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {channel.label}
          </span>
          <span className={cn('truncate text-[13px] text-foreground', channel.mono && 'font-mono')}>
            {value}
          </span>
        </span>
      </>
    );
    const className =
      'flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 transition-colors hover:border-hygge';
    return href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={`Open ${channel.label}`}
      >
        {content}
      </a>
    ) : (
      <button
        type="button"
        onClick={handleCopy}
        className={className}
        title={`Copy ${channel.label}`}
      >
        {content}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={!onAdd}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-dashed border-border bg-transparent px-3 py-2 text-left transition-colors',
        onAdd ? 'cursor-pointer hover:border-hygge hover:text-hygge' : 'cursor-default opacity-60'
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {channel.icon}
      </span>
      <span className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {channel.label}
        </span>
        <span className="text-[12px] text-muted-foreground">{onAdd ? '+ Add' : '—'}</span>
      </span>
    </button>
  );
}

interface PersonalContactsCardProps {
  personalInfo: PersonalInfo | null | undefined;
  firstName: string;
  canEdit: boolean;
  onEdit: () => void;
}

function PersonalContactsCard({
  personalInfo,
  firstName,
  canEdit,
  onEdit,
}: PersonalContactsCardProps) {
  const valueFor = (key: MessengerKey): string | null | undefined => {
    if (!personalInfo) return null;
    if (key === 'linkedin') return personalInfo.linkedinUrl;
    return personalInfo[key];
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
        <div className="flex items-baseline gap-2.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Personal Contacts
          </h3>
          <span className="text-[10px] text-muted-foreground/70">
            — how to reach {firstName} directly
          </span>
        </div>
        {canEdit && (
          <IconActionButton icon={Pencil} tooltip="Edit personal contacts" onClick={onEdit} />
        )}
      </header>
      <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
        {MESSENGER_KEYS.map((key) => (
          <PersonalChannelSlot
            key={key}
            channel={CHANNELS[key]}
            value={valueFor(key)}
            onAdd={canEdit ? onEdit : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Emergency contact card — avatar + name + inline action chips for every
// filled channel. Primary contacts get a thin hygge stripe on the left edge.
// ─────────────────────────────────────────────────────────────────────────────

interface EmergencyContactChipProps {
  channel: ChannelConfig;
  value: string;
}

function EmergencyContactChip({ channel, value }: EmergencyContactChipProps) {
  const href = channel.buildHref?.(value);
  const content = (
    <>
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
          channel.bg,
          channel.fg
        )}
      >
        {channel.icon}
      </span>
      <span className={cn('text-[12px] text-foreground', channel.mono && 'font-mono')}>
        {value}
      </span>
    </>
  );
  const className =
    'inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 transition-colors hover:border-hygge';
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      title={`Open ${channel.label}`}
    >
      {content}
    </a>
  ) : (
    <button
      type="button"
      onClick={() => void navigator.clipboard.writeText(value)}
      className={className}
      title={`Copy ${channel.label}`}
    >
      {content}
    </button>
  );
}

interface ContactCardProps {
  contact: EmergencyContact;
  onEdit?: () => void;
  onDelete?: () => void;
}

function ContactCard({ contact, onEdit, onDelete }: ContactCardProps) {
  const isPrimary = contact.isPrimary;
  // Each filled channel becomes an inline chip
  const filledChannels = (Object.entries(CHANNELS) as [ContactKey, ChannelConfig][])
    .map(([key, channel]) => {
      const raw = contact[key];
      return raw && raw.trim().length > 0 ? { key, channel, value: raw.trim() } : null;
    })
    .filter((x): x is { key: ContactKey; channel: ChannelConfig; value: string } => x !== null);

  return (
    <div
      className={cn(
        'group relative rounded-xl border bg-card py-4 pl-5 pr-4 transition-colors',
        isPrimary ? 'border-hygge/30' : 'border-border/60 hover:border-border'
      )}
    >
      {/* Primary stripe on left edge */}
      {isPrimary && (
        <span
          aria-hidden
          className="absolute left-0 top-3.5 bottom-3.5 w-[3px] rounded-r bg-hygge"
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Identity block: avatar + name + pills */}
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-hygge to-hygge/70 text-base font-semibold text-white shadow-sm">
            {getInitial(contact.name)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="truncate text-[15px] font-semibold text-foreground">{contact.name}</h4>
              {isPrimary && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-hygge/15 px-1.5 py-0.5 text-[10px] font-medium text-hygge">
                  <Star className="h-2.5 w-2.5 fill-current" />
                  Primary
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatRelationship(contact.relationship)}
            </div>
          </div>
        </div>

        {/* Action chips + hover actions */}
        <div className="flex flex-wrap items-center gap-1.5">
          {filledChannels.map(({ key, channel, value }) => (
            <EmergencyContactChip key={key} channel={channel} value={value} />
          ))}

          {(onEdit || onDelete) && (
            <div className="ml-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {onEdit && (
                <IconActionButton icon={Pencil} tooltip="Edit contact" onClick={onEdit} size="sm" />
              )}
              {onDelete && (
                <IconActionButton
                  icon={Trash2}
                  tooltip="Delete contact"
                  onClick={onDelete}
                  variant="destructive"
                  size="sm"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab
// ─────────────────────────────────────────────────────────────────────────────

export function EmergencyTab({ employee }: EmergencyTabProps) {
  const contacts = employee.emergencyContacts || [];
  const canEdit = usePermission('employees.emergency.edit');
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [personalContactsModalOpen, setPersonalContactsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<EmergencyContact | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    contactId: string | null;
    contactName: string;
  }>({
    open: false,
    contactId: null,
    contactName: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateEmergencyContactInput) => createEmergencyContact(employee.id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      setModalOpen(false);
    },
    onError: () => {
      toast.error('Failed to create emergency contact. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: UpdateEmergencyContactInput }) =>
      updateEmergencyContact(employee.id, contactId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      setModalOpen(false);
      setEditingContact(null);
    },
    onError: () => {
      toast.error('Failed to update emergency contact. Please try again.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => deleteEmergencyContact(employee.id, contactId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      setDeleteDialog({ open: false, contactId: null, contactName: '' });
    },
    onError: () => {
      toast.error('Failed to delete emergency contact. Please try again.');
    },
  });

  const personalContactsMutation = useMutation({
    mutationFn: (data: UpdatePersonalContactsInput) => updatePersonalContacts(employee.id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      setPersonalContactsModalOpen(false);
    },
    onError: () => {
      toast.error('Failed to update personal contacts. Please try again.');
    },
  });

  const handleAdd = () => {
    setEditingContact(null);
    setModalOpen(true);
  };

  const handleEdit = (contact: EmergencyContact) => {
    setEditingContact(contact);
    setModalOpen(true);
  };

  const handleDelete = (contact: EmergencyContact) => {
    setDeleteDialog({
      open: true,
      contactId: contact.id,
      contactName: contact.name,
    });
  };

  const confirmDelete = () => {
    if (deleteDialog.contactId) {
      deleteMutation.mutate(deleteDialog.contactId);
    }
  };

  const handleSave = async (data: CreateEmergencyContactInput | UpdateEmergencyContactInput) => {
    if (editingContact) {
      await updateMutation.mutateAsync({ contactId: editingContact.id, data });
    } else {
      await createMutation.mutateAsync(data as CreateEmergencyContactInput);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <PersonalContactsCard
          personalInfo={employee.personalInfo}
          firstName={employee.firstName}
          canEdit={canEdit}
          onEdit={() => setPersonalContactsModalOpen(true)}
        />

        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <header className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
            <div className="flex items-baseline gap-2.5">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Emergency Contacts
              </h3>
              {contacts.length > 0 && (
                <span className="text-[10px] text-muted-foreground/70">
                  — {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
                </span>
              )}
            </div>
          </header>
          <div className="p-4">
            {contacts.length > 0 ? (
              <div className="space-y-2.5">
                {contacts.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={canEdit ? () => handleEdit(contact) : undefined}
                    onDelete={canEdit ? () => handleDelete(contact) : undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-hygge/10">
                  <Users className="h-4.5 w-4.5 text-hygge" />
                </div>
                <p className="mb-1 text-sm font-medium text-foreground">No emergency contacts</p>
                <p className="text-xs text-muted-foreground">
                  Add emergency contacts for this employee
                </p>
              </div>
            )}

            {canEdit && (
              <Button
                variant="outline"
                className={cn(
                  'w-full gap-2 border-dashed text-hygge hover:bg-hygge/5 hover:text-hygge cursor-pointer',
                  contacts.length > 0 ? 'mt-3' : 'mt-4'
                )}
                onClick={handleAdd}
              >
                <Plus className="h-4 w-4" />
                Add Emergency Contact
              </Button>
            )}
          </div>
        </div>

        <CustomBlocksSection section="EMERGENCY" employeeId={employee.id} />
      </div>

      <EmergencyContactModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        employeeId={employee.id}
        contact={editingContact}
        onSave={handleSave}
      />

      <PersonalContactsModal
        open={personalContactsModalOpen}
        onOpenChange={setPersonalContactsModalOpen}
        personalInfo={employee.personalInfo}
        onSave={(input) => personalContactsMutation.mutateAsync(input)}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Emergency Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.contactName}</strong>? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
